import { useTranslation } from 'react-i18next';
import { StyledRobotRoutine } from './RobotRoutine.styles';
import { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import { StepsProgress } from 'lib/components/Progress/StepsProgress/StepsProgress';
import { BarProgress } from 'lib/components/Progress/BarProgress/BarProgress';
import {
  MachineConnectionStatus,
  RobotStatus,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { RobotUtils } from 'app/utils/robot/RobotUtils';

interface RobotRoutineProps {
  robot: Machine;
  showCountInHeader?: boolean;
  progressColors?: {
    completed: string;
    current: string;
    remaining: string;
  };
}

export const RobotRoutine = ({ robot, showCountInHeader = false, progressColors }: RobotRoutineProps): JSX.Element => {
  const { t } = useTranslation();

  const isRobotCleaning =
    robot.robotStatus === RobotStatus.Autonomous && robot.connectionStatus === MachineConnectionStatus.Online;

  const isRobotHaveRoutine = robot?.latestRoutine?.data;

  const routineName = robot?.latestRoutine?.data?.name;
  const numberOfRepeats = robot?.latestRoutine?.data?.numberOfRepeats || 0;
  const currentRepeat = Math.min(robot?.latestRoutine?.data?.currentRepeat || 0, numberOfRepeats);

  const getFormatSteps = (): string => String(currentRepeat).concat('/').concat(String(numberOfRepeats));

  if (!isRobotCleaning || !isRobotHaveRoutine) return <></>;

  const percent = RobotUtils.getPercentageData({ data: currentRepeat, total: numberOfRepeats });
  const title = routineName || t('robotDashboard.cleaningRoute.label.unNamedRoutine');

  // Colors only the already-filled steps: everything before the current one gets `completed`,
  // the last filled step gets `current`. Steps past `currentRepeat` stay unfilled and take
  // `trailColor` regardless of what's in this array.
  const stepStrokeColors = progressColors
    ? Array.from({ length: numberOfRepeats }, (_, index) =>
        index < currentRepeat - 1 ? progressColors.completed : progressColors.current
      )
    : undefined;

  return (
    <StyledRobotRoutine>
      {showCountInHeader ? (
        <div className="robot-routine__header">
          <p className="robot-routine__title robot-routine__title--header">{title}</p>
          <span className="robot-routine__count">{getFormatSteps()}</span>
        </div>
      ) : (
        <p className="robot-routine__title">{title}</p>
      )}
      <div className="robot-routine__body">
        {numberOfRepeats <= 50 ? (
          <StepsProgress
            className="robot-routine__routine"
            status="active"
            steps={numberOfRepeats}
            percent={percent}
            format={showCountInHeader ? undefined : getFormatSteps}
            showInfo={!showCountInHeader}
            strokeColor={stepStrokeColors}
            trailColor={progressColors?.remaining}
          />
        ) : (
          <BarProgress
            className="robot-routine__routine"
            percent={percent}
            size="small"
            status="active"
            format={showCountInHeader ? undefined : getFormatSteps}
            showInfo={!showCountInHeader}
            strokeColor={progressColors?.current}
            trailColor={progressColors?.remaining}
          />
        )}
      </div>
    </StyledRobotRoutine>
  );
};
